/**
 * Shared TMAX overlay markers used by SampledynagraphComponent and
 * SamplegraphdynanontimeComponent. Both hosts had byte-identical copies
 * of addTMAXShutDown / addTMAXStart / addTMAXEnd. The bodies live here
 * and are invoked via `addTMAX*.call(this)` so `this.focus`, `this.x`,
 * `this.y`, `this.TmaxShutDown`, `this.Tmaxstart`, and `this.TmaxEnd`
 * resolve to the caller's instance state.
 */

export interface DynaTmaxContext {
  focus: LegacyAny;
  x: LegacyAny;
  y: LegacyAny;
  Tmaxstart: LegacyAny;
  TmaxEnd: LegacyAny;
  TmaxShutDown: LegacyAny;
}

const TMAX_LINE_DASH = "5, 5";
const TMAX_TEXT_FONT = "10px Signify-Relative-Bold";
const TMAX_TEXT_ANCHOR_MIDDLE = "middle";
const TMAX_STROKE_COLOR = "grey";
const TMAX_TEXT_ANCHOR_ATTR = "text-anchor";
const TMAX_TEXT_COLOR = "lightgrey";
const TMAX_TEXT_ANCHOR_END = "end";
const TMAX_ELEMENT_LINE = "line";
const TMAX_ELEMENT_TEXT = "text";
const TMAX_STYLE_STROKE = "stroke";
const TMAX_STYLE_STROKE_DASHARRAY = "stroke-dasharray";
const TMAX_STYLE_FONT = "font";
const TMAX_STYLE_COLOR = "color";
const TMAX_ATTR_X = "x";
const TMAX_ATTR_Y = "y";
const TMAX_ATTR_X1 = "x1";
const TMAX_ATTR_Y1 = "y1";
const TMAX_ATTR_X2 = "x2";
const TMAX_ATTR_Y2 = "y2";
const TMAX_LINE_TOP_Y = 105;
const TMAX_LINE_BOTTOM_Y = 0;
const TMAX_TEXT_Y_STANDARD = -7;
const TMAX_TEXT_Y_END = -12;
const TMAX_LINE_LABEL_START = "T_MAX_START";
const TMAX_LINE_LABEL_END = "T_MAX_END";
const TMAX_TEXT_LABEL_SHUTDOWN = "T_Max_Shutdown";
const TMAX_TEXT_LABEL_START = "T_Max_Start";
const TMAX_TEXT_LABEL_END = "T_Max_End";

export function addTMAXShutDown(this: DynaTmaxContext) {
  this.focus
    .append(TMAX_ELEMENT_LINE)
    .style(TMAX_STYLE_STROKE, TMAX_STROKE_COLOR)
    .style(TMAX_STYLE_STROKE_DASHARRAY, TMAX_LINE_DASH)
    .text(TMAX_LINE_LABEL_START)
    .attr(TMAX_ATTR_X1, this.x(this.TmaxShutDown))
    .attr(TMAX_ATTR_Y1, this.y(TMAX_LINE_TOP_Y))
    .attr(TMAX_ATTR_X2, this.x(this.TmaxShutDown))
    .attr(TMAX_ATTR_Y2, this.y(TMAX_LINE_BOTTOM_Y));

  this.focus
    .append(TMAX_ELEMENT_TEXT)
    .attr(TMAX_ATTR_X, this.x(this.TmaxShutDown))
    .attr(TMAX_ATTR_Y, this.y(TMAX_TEXT_Y_STANDARD))
    .attr(TMAX_TEXT_ANCHOR_ATTR, TMAX_TEXT_ANCHOR_END)
    .style(TMAX_STYLE_FONT, TMAX_TEXT_FONT)
    .style(TMAX_STYLE_COLOR, TMAX_TEXT_COLOR)
    .style(TMAX_TEXT_ANCHOR_ATTR, TMAX_TEXT_ANCHOR_MIDDLE)
    .text(TMAX_TEXT_LABEL_SHUTDOWN);
}

export function addTMAXStart(this: DynaTmaxContext) {
  this.focus
    .append(TMAX_ELEMENT_LINE)
    .style(TMAX_STYLE_STROKE, TMAX_STROKE_COLOR)
    .style(TMAX_STYLE_STROKE_DASHARRAY, TMAX_LINE_DASH)
    .attr(TMAX_ATTR_X1, this.x(this.Tmaxstart))
    .attr(TMAX_ATTR_Y1, this.y(TMAX_LINE_TOP_Y))
    .attr(TMAX_ATTR_X2, this.x(this.Tmaxstart))
    .attr(TMAX_ATTR_Y2, this.y(TMAX_LINE_BOTTOM_Y));
  this.focus
    .append(TMAX_ELEMENT_TEXT)
    .attr(TMAX_ATTR_X, this.x(this.Tmaxstart))
    .attr(TMAX_ATTR_Y, this.y(TMAX_TEXT_Y_STANDARD))
    .attr(TMAX_TEXT_ANCHOR_ATTR, TMAX_TEXT_ANCHOR_END)
    .style(TMAX_STYLE_FONT, TMAX_TEXT_FONT)
    .style(TMAX_STYLE_COLOR, TMAX_TEXT_COLOR)
    .style(TMAX_TEXT_ANCHOR_ATTR, TMAX_TEXT_ANCHOR_MIDDLE)
    .text(TMAX_TEXT_LABEL_START);
}

export function addTMAXEnd(this: DynaTmaxContext) {
  this.focus
    .append(TMAX_ELEMENT_LINE)
    .style(TMAX_STYLE_STROKE, TMAX_STROKE_COLOR)
    .style(TMAX_STYLE_STROKE_DASHARRAY, TMAX_LINE_DASH)
    .text(TMAX_LINE_LABEL_END)
    .attr(TMAX_ATTR_X1, this.x(this.TmaxEnd))
    .attr(TMAX_ATTR_Y1, this.y(TMAX_LINE_TOP_Y))
    .attr(TMAX_ATTR_X2, this.x(this.TmaxEnd))
    .attr(TMAX_ATTR_Y2, this.y(TMAX_LINE_BOTTOM_Y));
  this.focus
    .append(TMAX_ELEMENT_TEXT)
    .attr(TMAX_ATTR_X, this.x(this.TmaxEnd))
    .attr(TMAX_ATTR_Y, this.y(TMAX_TEXT_Y_END))
    .attr(TMAX_TEXT_ANCHOR_ATTR, TMAX_TEXT_ANCHOR_END)
    .style(TMAX_STYLE_FONT, TMAX_TEXT_FONT)
    .style(TMAX_STYLE_COLOR, TMAX_TEXT_COLOR)
    .style(TMAX_TEXT_ANCHOR_ATTR, TMAX_TEXT_ANCHOR_MIDDLE)
    .text(TMAX_TEXT_LABEL_END);
}
